import * as React from 'react'
import ReactMedia from 'react-media'
import { useQuery } from '@apollo/client'
import { isEmpty, size } from 'lodash'
import { useRouter } from 'next/router'
import styled, { useTheme } from 'styled-components'
import { useFlagQueryFn } from 'toggled'

import Catalog from '~/components/catalog'
import Media from '~/components/media'
import { relatedProductsQuery } from '~/gql/queries'
import { useCarousel } from '~/hooks'
import Section from '~/screens/home/section'
import Button from '~/truck/button'
import Icon from '~/truck/icon'
import { flagQueries } from '~/utils/constants'

interface RelatedProductsProps {
  productId: string
}

interface SliderProps {
  products: unknown[]
  slidesToScroll: number
}

const StyledSection = styled(Section)`
  margin-top: ${props => props.theme.spacing.comfortable}px;
  width: 100%;
`

const StyledSliderContainer = styled.div`
  position: relative;
`

const StyledSliderViewport = styled.div`
  overflow: hidden;
  width: 100%;

  &.is-draggable {
    cursor: move;
    cursor: grab;
  }

  &.is-dragging {
    cursor: grabbing;
  }
`

const StyledCatalog = styled(Catalog)`
  -khtml-user-select: none;
  -webkit-tap-highlight-color: transparent;
  -webkit-touch-callout: none;
  display: flex;
  flex-wrap: nowrap;
  user-select: none;
`

// TODO: move shadows to `elevation` when the elevations are defined by design team
const SliderArrow = styled(Button.Circular)`
  filter: drop-shadow(0px 0px 0px rgba(63, 63, 68, 0.05)) drop-shadow(0px 1px 3px rgba(63, 63, 68, 0.15));
  margin: 0 ${props => props.theme.spacing.cozy}px;
  position: absolute;
  top: 48%;
  transform: translateY(-58%);
  z-index: 10;

  &:hover {
    background: ${props => props.theme.colors.white};
    filter: drop-shadow(0px 2px 2px rgba(63, 63, 68, 0.2)) drop-shadow(0px 1px 3px rgba(63, 63, 68, 0.15));

    > svg {
      fill: ${props => props.theme.colors.secondary};
    }
  }
`

function Slider(props: SliderProps) {
  const { products, slidesToScroll } = props

  const isDraggingRef = React.useRef(false)

  const carousel = useCarousel({
    slidesToScroll,
  })

  const router = useRouter()

  React.useEffect(() => {
    const handleScroll = () => {
      isDraggingRef.current = true
    }

    const handlePointerDown = () => {
      isDraggingRef.current = false
    }

    if (carousel.emblaApi) {
      carousel.emblaApi.reInit({ slidesToScroll })

      carousel.emblaApi.on('scroll', handleScroll)

      carousel.emblaApi.on('pointerDown', handlePointerDown)
    }

    return () => {
      carousel.emblaApi?.off('scroll', handleScroll)

      carousel.emblaApi?.off('pointerDown', handlePointerDown)
    }
  }, [carousel.emblaApi, slidesToScroll])

  if (size(products) <= slidesToScroll) {
    return <StyledCatalog products={products} />
  }

  const handleProductOnClick = (e, productUrl) => {
    e.preventDefault()

    if (!isDraggingRef.current) {
      router.push(productUrl).then(() => window.scrollTo(0, 0))
    }
  }

  return (
    <StyledSliderContainer>
      <StyledSliderViewport ref={carousel.ui.viewportRef}>
        <StyledCatalog productOnClick={handleProductOnClick} products={products} />
      </StyledSliderViewport>
      {carousel.ui.isPrevBtnEnabled && (
        <SliderArrow color="white" style={{ left: 0 }} onClick={carousel.ui.scrollToPrev}>
          <Icon type="caret-left" color="secondary" />
        </SliderArrow>
      )}
      {carousel.ui.isNextBtnEnabled && (
        <SliderArrow color="white" style={{ right: 0 }} onClick={carousel.ui.scrollToNext}>
          <Icon type="caret-right" color="secondary" />
        </SliderArrow>
      )}
    </StyledSliderContainer>
  )
}

const slidesToScrollWithFF = {
  lg: 3,
  xl: 4,
}

const slidesToScrollWithoutFF = {
  lg: 4,
  xl: 5,
}

export default function RelatedProducts(props: RelatedProductsProps) {
  const { productId } = props

  const { data, loading } = useQuery(relatedProductsQuery, {
    variables: {
      productId,
    },
  })

  const theme = useTheme()

  const products = data?.product?.relatedProducts

  const flagQueryFn = useFlagQueryFn()

  const isFullWidth = flagQueryFn(flagQueries.HAS_FULL_WIDTH)

  const slidesToScroll = isFullWidth ? slidesToScrollWithoutFF : slidesToScrollWithFF

  if (loading || isEmpty(products)) {
    return null
  }

  // FIXME: use Slider component
  return (
    <StyledSection title="Productos relacionados">
      <Media
        mobile={<Catalog products={products} collapsed />}
        desktop={() => (
          <ReactMedia query={{ minWidth: theme.breakpoints.xl }}>
            {isBigScreen =>
              // prettier-ignore
              <Slider
                products={products}
                slidesToScroll={isBigScreen ? slidesToScroll.xl : slidesToScroll.lg}
              />
            }
          </ReactMedia>
        )}
      />
    </StyledSection>
  )
}
